/**
 * How often a plan-of-care duty is performed, as one short string.
 *
 * Lives in `shared` because both front-ends show this column: the React patient
 * profile and the two AngularJS plan-of-care tables, which share one
 * `mapToTableRow`. A duty due twice on Monday and once on Sunday has to read the
 * same in all three, and the only way to keep that true is one function.
 */

/**
 * Week order, Monday first, matching the day columns beside this one in every
 * table that shows it — and the order the server writes the map in.
 *
 * Three letters rather than the columns' two: "MONx2" is read as a day and a
 * count, where "MOx2" reads as neither.
 */
const WEEK: ReadonlyArray<readonly [day: string, abbreviation: string]> = [
  ["MONDAY", "MON"],
  ["TUESDAY", "TUE"],
  ["WEDNESDAY", "WED"],
  ["THURSDAY", "THU"],
  ["FRIDAY", "FRI"],
  ["SATURDAY", "SAT"],
  ["SUNDAY", "SUN"],
];

/**
 * The map as it reaches the browser.
 *
 * The OpenAPI generator renders the server's `Record<string, number>` as
 * `{ [key: string]: unknown }`, so the counts arrive unknown and are narrowed
 * here rather than asserted — the alternative is a cast that would print
 * "MONxundefined" on any row that did not match it.
 */
export type PlanOfCareDutyTimesPerDayByDay = Record<string, unknown> | null | undefined;

/** A day's count, or null where the map holds anything that is not a real count. */
function dayCount(timesPerDayByDay: Record<string, unknown>, day: string): number | null {
  const count = timesPerDayByDay[day];
  return typeof count === "number" && Number.isFinite(count) && count > 0 ? count : null;
}

/**
 * `timesPerDayByDay` holds every day a duty is due, but only when the counts
 * differ between them — so its presence is exactly the case a single number
 * cannot describe, and it is rendered day by day: "MONx2, SUNx1".
 *
 * Otherwise the duty runs the same number of times on each of its days and
 * `timesPerDay` says it: "2x", or nothing at all for the ordinary once-a-day
 * duty, which would only add a column of "1x" to every row.
 *
 * A map that names no day it can read falls back to `timesPerDay` rather than
 * rendering an empty string, so a bad row understates rather than disappears.
 */
export function formatPlanOfCareDutyTimesPerDay(
  timesPerDay: number | null | undefined,
  timesPerDayByDay: PlanOfCareDutyTimesPerDayByDay
): string {
  if (timesPerDayByDay !== null && timesPerDayByDay !== undefined) {
    const parts: string[] = [];
    for (const [day, abbreviation] of WEEK) {
      const count = dayCount(timesPerDayByDay, day);
      if (count !== null) {
        parts.push(`${abbreviation}x${count}`);
      }
    }
    if (parts.length > 0) {
      return parts.join(", ");
    }
  }

  return timesPerDay === null || timesPerDay === undefined || timesPerDay <= 1
    ? ""
    : `${timesPerDay}x`;
}

/**
 * What to sort the column by: the busiest day, so a duty due twice on one day
 * sorts above one due once on all seven however the text reads.
 */
export function planOfCareDutyTimesPerDaySortValue(
  timesPerDay: number | null | undefined,
  timesPerDayByDay: PlanOfCareDutyTimesPerDayByDay
): number {
  const counts =
    timesPerDayByDay === null || timesPerDayByDay === undefined
      ? []
      : WEEK.map(([day]) => dayCount(timesPerDayByDay, day)).filter(
          (count): count is number => count !== null
        );

  return counts.length > 0 ? Math.max(...counts) : timesPerDay ?? 1;
}
